/**
 * Wave C (decision 0071, spec `docs/design/wave-c/wave-c-spec.md` §3, §7): the agent page's reads and the shapes the
 * clock's tools share. Every persistent agent has a page (Now · Knows · History · About · Settings); the Project DO
 * answers these reads (`ProjectRpcs.AgentNow`, `AgentHistory`, `MemoryHistory`, `AgentByOldHandle`) and the web only
 * renders them. Core `agentOverview` builds {@link AgentNowView}; the turn pack renders the same rows as `## Now`.
 *
 * No I/O.
 */
import { Schema } from "effect"
import { Actor, EntityRef, Principal } from "./common"
import { MemoryItem } from "./domain/knowledge"
import { AgentId, CommentId, Handle, ItemId, Millis, NonNegInt, PosInt, ProposalId, RunId, Seq } from "./ids"
import { Unmet } from "./placement"
import { Run } from "./run"

// #region now
/**
 * What a Now row is about. Doing now: `turn` (her live turn), `run` (a live run on an item she owns). Tracking: `goal`,
 * `milestone`, `kpi`. Open loops: `card` (an open proposal), `ask` (an open ask to her or from her), `hold` (a held
 * wake), `pr` (an open pull request), `stuck` (an item stuck on a blocker). Since her last turn: `woke` (a wake she got),
 * `quiet` (a change by a person to her item that woke nobody; it offers Tell).
 */
export const NowRowKind = Schema.Literals([
  "turn",
  "run",
  "goal",
  "milestone",
  "kpi",
  "card",
  "ask",
  "hold",
  "pr",
  "stuck",
  "woke",
  "quiet",
])
export type NowRowKind = typeof NowRowKind.Type

/**
 * Whose move an open loop is: a person's (`waitingOn` names them; the web shows ◆ when it is the viewer), hers, or
 * stuck. Absent on rows outside Open loops.
 */
export const NowMove = Schema.Literals(["person", "her", "stuck"])
export type NowMove = typeof NowMove.Type

/**
 * The one action a row offers. `open`: its subject's page. `decide`: the card. `answer`: the ask. `tell`: Tell Lea, a
 * comment on the item that @mentions her (`PostComment`, which wakes her as `human_mention`).
 */
export const NowAction = Schema.TaggedUnion({
  open: {},
  decide: { proposalId: ProposalId },
  answer: { askId: CommentId },
  tell: { itemId: ItemId },
})
export type NowAction = typeof NowAction.Type

/** One row on Now and in the pack's `## Now`. Words are built by core; the web adds no meaning. */
export const NowRow = Schema.Struct({
  /** Stable per row ("run:run_…", "card:prop_…", "woke:<runId>:<n>"). */
  key: Schema.String,
  kind: NowRowKind,
  /** The plain line ("W-46 on DIC-137 · 12 min · jannik-mbp"). ≤ 300 characters. */
  text: Schema.String.check(Schema.isMaxLength(300)),
  /** Always set on Doing now and Open loops: what woke the turn ("woke for: W-46 finished DIC-137") or what it waits for. */
  why: Schema.NullOr(Schema.String.check(Schema.isMaxLength(300))),
  subject: Schema.NullOr(EntityRef),
  /** The subject's short ref (DIC-12, P-3, G-1, R-9), when it has one. */
  ref: Schema.NullOr(Schema.String),
  /** Since when (the row's age). */
  at: Schema.NullOr(Millis),
  /** Open loops: whose move. */
  move: Schema.optionalKey(NowMove),
  /** The person or agent the row waits on, or who asked (a run's Asked by). */
  waitingOn: Schema.optionalKey(Schema.NullOr(Principal)),
  action: Schema.NullOr(NowAction),
  /**
   * Incident 2026-10-15: a waiting turn's or run's reasons (`RunState.waiting.unmet`), so the page says why in the
   * viewer's own words and clock ("Claude subscription (claude-jung) is rate limited until 01:40"). Absent otherwise.
   */
  waiting: Schema.optionalKey(Schema.Array(Unmet)),
})
export type NowRow = typeof NowRow.Type

/** A section: at most 25 rows on the page, then "and `more` more". */
export const NowSection = Schema.Struct({ rows: Schema.Array(NowRow), more: NonNegInt })
export type NowSection = typeof NowSection.Type

/** The daily check's quiet line ("Daily check at 09:00 · last found 2 loose ends Tue · woke her"). */
export const DailyCheckLine = Schema.Struct({
  /** null = off (`Cadence.dailyCheckHour: null`). */
  hour: Schema.NullOr(Schema.Int),
  tz: Schema.String,
  /** Last run that wrote (state.dailyCheck.lastAt); null = never. */
  lastAt: Schema.NullOr(Millis),
  /** Loose ends open at `lastAt` whose subjects are hers. */
  found: NonNegInt,
  /** The last check that woke her; null = never. */
  wokeAt: Schema.NullOr(Millis),
})
export type DailyCheckLine = typeof DailyCheckLine.Type

/** `ProjectRpcs.AgentNow`: Now, built by core `agentOverview` over state, her recent turns and comments. */
export const AgentNowView = Schema.Struct({
  agentId: AgentId,
  /** When it was built. */
  at: Millis,
  doingNow: NowSection,
  tracking: NowSection,
  openLoops: NowSection,
  /** Since her last turn ended (`since`; null = she never finished a turn). */
  since: Schema.Struct({ from: Schema.NullOr(Millis), rows: Schema.Array(NowRow), more: NonNegInt }),
  /** Null for an agent the daily check does not wake (none in C: every agent shows it). */
  dailyCheck: Schema.NullOr(DailyCheckLine),
})
export type AgentNowView = typeof AgentNowView.Type

/** The pack's `## Now` and the page's caps (spec §3.2). */
export const NOW_PAGE_ROWS = 25
export const NOW_PACK_ROWS = 25
// #endregion now

// #region history
/** History's filter chips. `runs`: her workers and validators on items she owns. */
export const AgentHistoryFilter = Schema.Literals(["all", "turns", "runs"])
export type AgentHistoryFilter = typeof AgentHistoryFilter.Type

/**
 * `ProjectRpcs.AgentHistory`: her runs newest first, whole days at a time (`runs_agent_idx`). `nextBefore`: pass it as
 * `before` for the next (older) page; null = nothing older. Open cards, asks and recent memory come from state.
 */
export const AgentHistoryPage = Schema.Struct({
  agentId: AgentId,
  runs: Schema.Array(Run),
  nextBefore: Schema.NullOr(Millis),
})
export type AgentHistoryPage = typeof AgentHistoryPage.Type
// #endregion history

// #region memory history
/** One revision of a memory, from the event that wrote it (`MemoryItemCreated`, `Revised`, `Archived`, `Revoked`). */
export const MemoryRevision = Schema.Struct({
  /** The item's revision after the event (archive and revoke keep the revision they hid). */
  rev: PosInt,
  op: Schema.Literals(["created", "revised", "archived", "revoked"]),
  seq: Seq,
  at: Millis,
  by: Actor,
  /** `EventMeta.approvedBy.by`: the person who accepted an agent's card; null when applied directly. */
  approvedBy: Schema.NullOr(Actor),
  /** The item as written (created, revised); null for archived and revoked. */
  item: Schema.NullOr(MemoryItem),
  /** Archive and revoke: why. */
  reason: Schema.NullOr(Schema.String),
})
export type MemoryRevision = typeof MemoryRevision.Type

/** `ProjectRpcs.MemoryHistory`: newest first; `total` counts every revision. */
export const MemoryHistory = Schema.Struct({ total: NonNegInt, revisions: Schema.Array(MemoryRevision) })
export type MemoryHistory = typeof MemoryHistory.Type
// #endregion memory history

// #region old handle
/**
 * `ProjectRpcs.AgentByOldHandle`: the agent whose earlier handle `handle` was (from `AgentUpdated.before.handle`), with
 * her current handle, so the page redirects. Null: no agent ever had it.
 */
export const AgentByOldHandleAnswer = Schema.NullOr(Schema.Struct({ agentId: AgentId, handle: Handle }))
export type AgentByOldHandleAnswer = typeof AgentByOldHandleAnswer.Type
// #endregion old handle

// #region monitors
/** "HH:MM", 24-hour. */
export const ClockTime = Schema.String.check(Schema.isPattern(/^(?:[01][0-9]|2[0-3]):[0-5][0-9]$/))
export type ClockTime = typeof ClockTime.Type

/**
 * When a monitor fires, as people say it (the sheet and `propose_monitor`). Core `cronOfWhen` turns it into
 * `trigger: cron{expr, tz: the project's zone}`: every N hours, every day at HH:MM, every week on a weekday at HH:MM.
 * Custom cron supports the same minute-resolution parser as recurring items.
 */
export const MonitorWhen = Schema.TaggedUnion({
  every_hours: { n: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 24 })) },
  daily: { at: ClockTime },
  /** 0 = Sunday … 6 = Saturday. */
  weekly: { weekday: Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 6 })), at: ClockTime },
  cron: { expr: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(300)) },
})
export type MonitorWhen = typeof MonitorWhen.Type

/** Quiet hours in the project's zone. Default for every-N-hours: 20:00 to 08:00 (core `DEFAULT_QUIET`). */
export const MonitorQuiet = Schema.Struct({ from: ClockTime, to: ClockTime })
export type MonitorQuiet = typeof MonitorQuiet.Type
// #endregion monitors

// #region a2
/**
 * Wave C A2: one command a platform turn's run already committed before a restart (`command_dedupe` rows keyed
 * `mcp:<runId>:…`, with the events each produced). The recovery system line lists them so the model does not repeat
 * them ("post_comment on DIC-134 (C-88)").
 */
export const RunCommit = Schema.Struct({
  /** The graph tool, when the row records it; null = unknown (the line names the event). */
  tool: Schema.NullOr(Schema.String),
  /** The ref the command produced or touched (DIC-160, P-3), when there is one. */
  ref: Schema.NullOr(Schema.String),
  /** The first event's tag (CommentPosted, ItemCreated, …). */
  eventTag: Schema.String,
  runId: RunId,
})
export type RunCommit = typeof RunCommit.Type
// #endregion a2
